import { useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import {
  useChangelogEntries,
  useMarkChangelogViewed,
} from "@/lib/api/changelog";
import { useChangelogStore } from "@/lib/hooks/use-changelog-store";
import { ChangelogEntry } from "./changelog-entry";

export function WhatsNewModal() {
  const isOpen = useChangelogStore((s) => s.isModalOpen);
  const closeModal = useChangelogStore((s) => s.closeModal);

  const { data: entries, isLoading } = useChangelogEntries(isOpen);
  const markViewed = useMarkChangelogViewed();

  const markedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen || !entries) return;
    for (const entry of entries) {
      if (!entry.is_read && !markedRef.current.has(entry.id)) {
        markedRef.current.add(entry.id);
        markViewed.mutate(entry.id);
      }
    }
  }, [isOpen, entries, markViewed]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="max-h-[90vh] max-w-[1100px] gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-border px-14 py-10">
          <DialogTitle className="text-xl font-bold tracking-tight">
            What&apos;s New
          </DialogTitle>
          <DialogDescription className="text-base">
            Updates and new features
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto px-14 py-10">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : !entries || entries.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No updates yet. Check back soon!
            </p>
          ) : (
            <div className="flex flex-col gap-14">
              {entries.map((entry) => (
                <ChangelogEntry key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
